import React from 'react';
import type { ModalChildrenProps } from '@noths/polaris-client-ribbons-design-system';
import { IconCheckmark, Modal } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { getProductImageURL, priceFormatter } from '@noths/polaris-client-utils';
import { Button } from '@noths/polaris-design-system-core-components';

import { BASKET_PAGE_PATH } from 'src/constants/page';
import type { BasketState } from 'src/redux/basket/slice';
import type { ProductState } from 'src/redux/product/types';
import {
  BASKET_MODAL_BUTTON_CONTINUE,
  BASKET_MODAL_BUTTON_CTA,
  BASKET_MODAL_CONTENT_LABEL,
} from '../copy';
import { SmallBusinessLove } from './SmallBusinessLove';
import * as styles from './BasketModal.styles';

export interface BasketModalStateProps {
  basket: BasketState['basket'];
  open: boolean;
  product: ProductState;
  quantity: number;
}

export interface BasketModalDispatchProps {
  onClose: VoidFunction;
  onNavigateToBasket: VoidFunction;
}

export type BasketModalProps = BasketModalDispatchProps & BasketModalStateProps;

const BasketSummary = ({ basket }: Pick<BasketModalProps, 'basket'>) => {
  const hasDiscount =
    basket?.itemsTotal && basket?.discountedItemsTotal
      ? basket.itemsTotal.cents > basket.discountedItemsTotal?.cents
      : false;
  const discountedTotalFormatted = basket?.discountedItemsTotal
    ? priceFormatter(basket.discountedItemsTotal.currency, basket.discountedItemsTotal.cents)
    : null;
  const itemsTotalFormatted = basket?.itemsTotal
    ? priceFormatter(basket.itemsTotal.currency, basket.itemsTotal.cents)
    : null;
  const itemsCount = basket?.itemsCount || 0;

  return (
    <div css={styles.summary}>
      {discountedTotalFormatted && (
        <p>
          <b>
            Basket total: {discountedTotalFormatted}
            {hasDiscount && <s>{itemsTotalFormatted}</s>}
          </b>
        </p>
      )}
      {itemsCount > 0 && (
        <p>
          {`${basket!.itemsCount} ${itemsCount === 1 ? 'item' : 'items'}`}
          <span css={visuallyHidden}> in the basket</span>
        </p>
      )}
    </div>
  );
};

const CTAButtons = ({
  onClose,
  onNavigateToBasket,
}: Pick<BasketModalProps, 'onNavigateToBasket' | 'onClose'>) => {
  return (
    <div css={styles.buttonContainer}>
      <Button onClick={onClose} secondary>
        {BASKET_MODAL_BUTTON_CONTINUE}
      </Button>
      <Button
        href={BASKET_PAGE_PATH}
        onClick={(e) => {
          e.preventDefault();
          onNavigateToBasket();
        }}
        role="button"
      >
        {BASKET_MODAL_BUTTON_CTA}
      </Button>
    </div>
  );
};

export const BasketModal = ({
  basket,
  onClose,
  onNavigateToBasket,
  open,
  product: { images, partner, title },
  quantity,
}: BasketModalProps) => {
  return (
    <>
      <Modal
        appRootSelector="#NOTHSPageWrapper"
        contentLabel={BASKET_MODAL_CONTENT_LABEL}
        isModalOpen={open}
        onModalClose={onClose}
      >
        {({ Body, Footer, Header }: ModalChildrenProps) => (
          <>
            <Header notificationStatus="success" onCloseButtonClick={onClose}>
              <div css={styles.headerSection}>
                <IconCheckmark height={24} width={24} />
                <h2>
                  <b>Item added to basket</b>
                  <br />
                  Great choice!
                </h2>
              </div>
            </Header>
            <Body>
              <div css={styles.bodyContent}>
                <div css={styles.addedProductSection}>
                  <img
                    alt=""
                    css={styles.productImage}
                    height={135}
                    src={getProductImageURL(images[0].href, 'mini')}
                    width={135}
                  />
                  <div>
                    <span>{partner.name}</span>
                    <p>{title}</p>
                    <p>Qty: {quantity}</p>
                  </div>
                </div>
                <div css={styles.contentSection}>
                  <SmallBusinessLove />
                </div>
              </div>
            </Body>
            <Footer>
              <BasketSummary basket={basket} />
              <CTAButtons onClose={onClose} onNavigateToBasket={onNavigateToBasket} />
            </Footer>
          </>
        )}
      </Modal>
      <styles.BasketModalGlobalStyles />
    </>
  );
};
